import { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import type * as GeoJSON from "geojson";
import type { Map as MapboxMap } from "mapbox-gl";

import {
  CompareSide,
  SceneCompare,
  SceneLegend,
  StoryCamera,
} from "../../types/storyMap";
import StoryMapCanvas, { StoryMapCanvasHandle } from "./StoryMapCanvas";
import StoryFuel3DLayers from "./StoryFuel3DLayers";
import {
  StorySceneSources,
  useCompareSideLayers,
} from "./useStorySceneLayers";
import StoryLegend from "./StoryLegendCard";
import { mergeLegendRows } from "./storyLegend";

export interface CompareMapViewHandle {
  /** Current camera (builder "Use current view" support). */
  getCamera: () => StoryCamera | null;
}

interface CompareMapViewProps {
  compare: SceneCompare;
  /** Scene camera (both sides stay camera-synced). */
  camera: StoryCamera | null;
  isPublic: boolean;
  /** Story-wide layer sources (frozen mapData public, live authed). */
  sources: StorySceneSources;
  /** Initial fit fallback when no camera at all (project boundary). */
  fitTo?: GeoJSON.Feature | null;
  interactive?: boolean;
  /** Scene legend (union of both sides' layers) drawn over the split view. */
  legend?: SceneLegend | null;
}

/**
 * Scene-mode before/after compare: two camera-synced StoryMapCanvas instances
 * filling their container, the "after" map revealed via clip-path by a
 * draggable handle. Shared by the builder's live map panel and the viewer's
 * full-viewport sticky map.
 */
const CompareMapView = forwardRef<CompareMapViewHandle, CompareMapViewProps>(
  (
    { compare, camera, isPublic, sources, fitTo, interactive = true, legend },
    ref
  ) => {
    const [position, setPosition] = useState(50);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const draggingRef = useRef(false);
    const beforeRef = useRef<StoryMapCanvasHandle | null>(null);
    const afterRef = useRef<StoryMapCanvasHandle | null>(null);
    // Map instances for the 3D fuel stands (mounted outside the canvases).
    const [beforeMap, setBeforeMap] = useState<MapboxMap | null>(null);
    const [afterMap, setAfterMap] = useState<MapboxMap | null>(null);
    // Guard so a sync-driven jumpTo's own moveend doesn't echo back.
    const syncingRef = useRef(false);

    useImperativeHandle(ref, () => ({
      getCamera: () => beforeRef.current?.getCamera() ?? null,
    }));

    const {
      layers: beforeLayers,
      legendRows: beforeRows,
      fuel3d: beforeFuel3d,
    } = useCompareSideLayers(compare.before, "before", "cmp-before", isPublic, sources);
    const {
      layers: afterLayers,
      legendRows: afterRows,
      fuel3d: afterFuel3d,
    } = useCompareSideLayers(compare.after, "after", "cmp-after", isPublic, sources);
    const legendRows = legend?.enabled ? mergeLegendRows(beforeRows, afterRows) : [];

    const handleMoveEnd = (source: "before" | "after") => (cam: StoryCamera) => {
      if (syncingRef.current) {
        syncingRef.current = false;
        return;
      }
      syncingRef.current = true;
      const other = source === "before" ? afterRef : beforeRef;
      other.current?.jumpTo(cam);
    };

    const updateFromClientX = (clientX: number) => {
      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) return;
      const pct = ((clientX - rect.left) / rect.width) * 100;
      setPosition(Math.max(0, Math.min(100, pct)));
    };

    const sideLabel = (side: CompareSide, edge: "left" | "right") => (
      <Box
        sx={{
          position: "absolute",
          top: 12,
          [edge]: 12,
          display: "flex",
          flexDirection: "column",
          alignItems: edge === "left" ? "flex-start" : "flex-end",
          gap: 0.5,
          pointerEvents: "none",
          zIndex: 3,
        }}
      >
        <Typography
          variant="caption"
          sx={{
            px: 1,
            py: 0.25,
            bgcolor: "rgba(33, 43, 55, 0.85)",
            color: "white",
            borderRadius: 0.5,
            fontWeight: 700,
            letterSpacing: 0.8,
          }}
        >
          {side.label}
        </Typography>
        {side.tilesStale && (
          <Typography
            variant="caption"
            sx={{
              px: 1,
              py: 0.25,
              bgcolor: "rgba(178, 106, 0, 0.9)",
              color: "white",
              borderRadius: 0.5,
            }}
          >
            Some layers couldn't be frozen
          </Typography>
        )}
      </Box>
    );

    return (
      <Box
        ref={containerRef}
        sx={{
          position: "relative",
          width: "100%",
          height: "100%",
          overflow: "hidden",
          userSelect: "none",
        }}
      >
        {/* Before (left, full) */}
        <Box sx={{ position: "absolute", inset: 0 }}>
          <StoryMapCanvas
            ref={beforeRef}
            layers={beforeLayers}
            fitTo={fitTo}
            initialBounds={fitTo}
            camera={camera}
            transition="jump"
            interactive={interactive}
            onMoveEnd={handleMoveEnd("before")}
            onMapReady={setBeforeMap}
          />
          <StoryFuel3DLayers map={beforeMap} items={beforeFuel3d} crownsEnabled={!isPublic} />
        </Box>
        {/* After (revealed from the right by the handle) */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            clipPath: `inset(0 0 0 ${position}%)`,
          }}
        >
          <StoryMapCanvas
            ref={afterRef}
            layers={afterLayers}
            fitTo={fitTo}
            initialBounds={fitTo}
            camera={camera}
            transition="jump"
            interactive={interactive}
            onMoveEnd={handleMoveEnd("after")}
            onMapReady={setAfterMap}
          />
          <StoryFuel3DLayers map={afterMap} items={afterFuel3d} crownsEnabled={!isPublic} />
        </Box>

        {sideLabel(compare.before, "left")}
        {sideLabel(compare.after, "right")}

        {/* Divider line */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            bottom: 0,
            left: `${position}%`,
            width: 2,
            bgcolor: "white",
            transform: "translateX(-1px)",
            pointerEvents: "none",
            zIndex: 2,
          }}
        />
        {/* Draggable handle strip */}
        <Box
          onPointerDown={(e) => {
            draggingRef.current = true;
            (e.target as HTMLElement).setPointerCapture(e.pointerId);
            updateFromClientX(e.clientX);
          }}
          onPointerMove={(e) => {
            if (draggingRef.current) updateFromClientX(e.clientX);
          }}
          onPointerUp={() => {
            draggingRef.current = false;
          }}
          sx={{
            position: "absolute",
            top: 0,
            bottom: 0,
            left: `${position}%`,
            width: 24,
            transform: "translateX(-12px)",
            cursor: "ew-resize",
            touchAction: "none",
            zIndex: 3,
          }}
        />
        {/* Handle knob */}
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: `${position}%`,
            transform: "translate(-50%, -50%)",
            width: 32,
            height: 32,
            borderRadius: "50%",
            bgcolor: "white",
            boxShadow: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#212b37",
            fontWeight: 700,
            fontSize: 13,
            pointerEvents: "none",
            zIndex: 3,
          }}
        >
          {"⇔"}
        </Box>
        <StoryLegend rows={legendRows} legend={legend} />
      </Box>
    );
  }
);

CompareMapView.displayName = "CompareMapView";

export default CompareMapView;
